import { useMemo, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { CaretDown as CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown'
import { DownloadSimple as DownloadIcon } from '@phosphor-icons/react/dist/ssr/DownloadSimple'
import { Image as ImageIcon } from '@phosphor-icons/react/dist/ssr/Image'
import { Link as RouterLink } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, XAxis, YAxis } from 'recharts'

import { CABINET_PAIR_COLORS } from '@/lib/assistant-cabinet-heatmap'
import { captureElementAsPng, downloadCsv } from '@/lib/assistant-export'
import { normalizeAssistantNavigatePath } from '@/lib/assistant-navigate'
import type { AssistantWidget, PromotionSuggestionItem } from '@/lib/assistant-widgets'
import { chipThumbnailUrl } from '@/lib/images'

import { ProductSwapCard } from './product-swap-card'
import { RoutePlanMap } from './route-plan-map'
import { PromotionFormWidget, type PromotionDraftAction } from './promotion-form-widget'

function WidgetShell({
	title,
	subtitle,
	children,
	exportRef,
	csvRows,
	filenameBase,
}: {
	title: string
	subtitle?: string
	children: React.ReactNode
	exportRef?: React.RefObject<HTMLDivElement | null>
	csvRows?: string[][]
	filenameBase?: string
}) {
	const handleCsv = () => {
		if (!csvRows?.length || !filenameBase) return
		downloadCsv(`${filenameBase}.csv`, csvRows)
	}
	const handlePng = async () => {
		if (!exportRef?.current || !filenameBase) return
		await captureElementAsPng(exportRef.current, `${filenameBase}.png`)
	}

	return (
		<Box
			sx={{
				mt: 1.5,
				p: 1.25,
				borderRadius: 1.5,
				border: '1px solid',
				borderColor: 'divider',
				bgcolor: 'background.default',
			}}
		>
			<Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
				<Box sx={{ minWidth: 0 }}>
					<Typography sx={{ fontWeight: 600, fontSize: '0.8125rem' }} variant="body2">
						{title}
					</Typography>
					{subtitle ? (
						<Typography color="text.secondary" sx={{ fontSize: '0.75rem' }} variant="caption">
							{subtitle}
						</Typography>
					) : null}
				</Box>
				{(csvRows || exportRef) && filenameBase ? (
					<Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
						{csvRows ? (
							<Tooltip title="Export CSV">
								<IconButton aria-label="Export CSV" onClick={handleCsv} size="small">
									<DownloadIcon size={16} />
								</IconButton>
							</Tooltip>
						) : null}
						{exportRef ? (
							<Tooltip title="Export image">
								<IconButton aria-label="Export image" onClick={() => void handlePng()} size="small">
									<ImageIcon size={16} />
								</IconButton>
							</Tooltip>
						) : null}
					</Stack>
				) : null}
			</Stack>
			<Box ref={exportRef} sx={{ mt: 1 }}>
				{children}
			</Box>
		</Box>
	)
}

function formatPct(value: number | null | undefined): string {
	if (value == null || Number.isNaN(value)) return '—'
	const sign = value > 0 ? '+' : ''
	return `${sign}${value.toFixed(1)}%`
}

function PeriodComparisonWidget({ data }: { data: Extract<AssistantWidget, { type: 'period_comparison' }>['data'] }) {
	const exportRef = useRef<HTMLDivElement>(null)
	return (
		<WidgetShell
			exportRef={exportRef}
			filenameBase="period-comparison"
			subtitle={`${data.recent.from} – ${data.recent.to}${data.recent.until ? ' (so far)' : ''} vs same period last week`}
			title="Week over week"
		>
			<Stack direction="row" spacing={2}>
				<Box>
					<Typography color="text.secondary" variant="caption">
						Revenue
					</Typography>
					<Typography sx={{ fontWeight: 700 }} variant="body2">
						${(data.recent.revenue_dollars ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
					</Typography>
					<Typography
						color={data.change.revenue_pct != null && data.change.revenue_pct < 0 ? 'error.main' : 'success.main'}
						variant="caption"
					>
						{formatPct(data.change.revenue_pct)} vs prior
					</Typography>
				</Box>
				<Box>
					<Typography color="text.secondary" variant="caption">
						Orders
					</Typography>
					<Typography sx={{ fontWeight: 700 }} variant="body2">
						{data.recent.orders ?? 0}
					</Typography>
					<Typography variant="caption">{formatPct(data.change.orders_pct)} vs prior</Typography>
				</Box>
			</Stack>
		</WidgetShell>
	)
}

function RestockPrioritiesWidget({ data }: { data: Extract<AssistantWidget, { type: 'restock_priorities' }>['data'] }) {
	const exportRef = useRef<HTMLDivElement>(null)
	const csvRows = [
		['Cabinet', 'Slot', 'Product', 'Status', 'Qty', 'Revenue'],
		...data.items.map((item) => [
			item.site_name,
			item.slot_label ?? String(item.cell_index),
			item.product_name ?? '',
			item.stock_status,
			String(item.quantity ?? ''),
			String((item.revenue_cents ?? 0) / 100),
		]),
	]
	return (
		<WidgetShell
			csvRows={csvRows}
			exportRef={exportRef}
			filenameBase="restock-priorities"
			subtitle={`${data.count} slot${data.count === 1 ? '' : 's'} need attention`}
			title="Restock priorities"
		>
			<Stack spacing={0.75} ref={exportRef}>
				{data.items.map((item, index) => (
					<Stack key={`${item.site_id}-${item.cell_index}-${index}`} direction="row" spacing={1} alignItems="center">
						<Chip
							color={item.stock_status === 'out' ? 'error' : 'warning'}
							label={item.stock_status === 'out' ? 'OOS' : 'Low'}
							size="small"
							sx={{ height: 20, fontSize: '0.625rem' }}
						/>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }} variant="body2">
								{item.product_name ?? 'Empty slot'} • {item.slot_label ?? `#${item.cell_index}`}
							</Typography>
							<Typography color="text.secondary" sx={{ fontSize: '0.6875rem' }} variant="caption">
								{item.site_name}
								{item.units ? ` • ${item.units} sold (7d)` : ''}
							</Typography>
						</Box>
						{item.navigate ? (
							<Chip
								clickable
								color="primary"
								component={RouterLink}
								label="Open"
								size="small"
								sx={{ flexShrink: 0 }}
								to={normalizeAssistantNavigatePath(item.navigate.path)}
								variant="outlined"
							/>
						) : null}
					</Stack>
				))}
			</Stack>
		</WidgetShell>
	)
}

function formatDriveMinutes(seconds: number): string {
	if (seconds <= 0) return '0 min'
	if (seconds < 60) return '<1 min'
	return `${Math.round(seconds / 60)} min`
}

function StoreVisitPlanWidget({ data }: { data: Extract<AssistantWidget, { type: 'store_visit_plan' }>['data'] }) {
	const mapStops = useMemo(
		() =>
			data.stops
				.filter((stop) => typeof stop.latitude === 'number' && typeof stop.longitude === 'number')
				.map((stop) => ({
					order: stop.order,
					label: stop.site_name,
					latitude: stop.latitude!,
					longitude: stop.longitude!,
				})),
		[data.stops],
	)

	const routeSummary = [
		data.summary,
		data.route?.total_duration_seconds
			? `~${formatDriveMinutes(data.route.total_duration_seconds)} drive${data.route.estimated ? ' (est.)' : ''}`
			: null,
	]
		.filter(Boolean)
		.join(' • ')

	return (
		<WidgetShell subtitle={routeSummary} title={data.fleet_wide ? 'Fleet visit plan' : 'Store visit plan'}>
			{mapStops.length ? <RoutePlanMap polyline={data.route?.polyline} stops={mapStops} /> : null}
			<Stack spacing={0.75}>
				{data.stops.map((stop) => (
					<Stack key={stop.site_id} direction="row" spacing={1} alignItems="center">
						<Chip label={`#${stop.order}`} size="small" sx={{ height: 22, minWidth: 32, flexShrink: 0 }} />
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }} variant="body2">
								{stop.site_name}
								{stop.store_name ? ` • ${stop.store_name}` : ''}
							</Typography>
							<Typography color="text.secondary" sx={{ fontSize: '0.6875rem' }} variant="caption">
								{[
									stop.leg
										? `${formatDriveMinutes(stop.leg.duration_seconds)} drive${stop.leg.estimated ? ' (est.)' : ''}`
										: null,
									...stop.reasons.slice(0, 2),
									typeof stop.latitude !== 'number' ? 'no location' : null,
								]
									.filter(Boolean)
									.join(' • ')}
							</Typography>
						</Box>
						{stop.navigate ? (
							<Chip
								clickable
								color="primary"
								component={RouterLink}
								label="Open"
								size="small"
								sx={{ flexShrink: 0 }}
								to={normalizeAssistantNavigatePath(stop.navigate.path)}
								variant="outlined"
							/>
						) : null}
					</Stack>
				))}
			</Stack>
		</WidgetShell>
	)
}

function PendingPlanogramWidget({ data }: { data: Extract<AssistantWidget, { type: 'pending_planogram' }>['data'] }) {
	if (!data.has_pending) {
		return (
			<WidgetShell subtitle={data.note} title="Pending planogram">
				<Typography color="text.secondary" sx={{ fontSize: '0.75rem' }} variant="body2">
					No draft awaiting review.
				</Typography>
			</WidgetShell>
		)
	}
	return (
		<WidgetShell
			subtitle={`${data.change_count ?? 0} change${(data.change_count ?? 0) === 1 ? '' : 's'} • ${data.pending_approval_status ?? 'pending'}`}
			title="Pending planogram review"
		>
			{data.navigate ? (
				<Button
					color="primary"
					component={RouterLink}
					size="small"
					to={normalizeAssistantNavigatePath(data.navigate.path)}
					variant="contained"
				>
					{data.navigate.label}
				</Button>
			) : null}
		</WidgetShell>
	)
}

function VisionRiskWidget({ data }: { data: Extract<AssistantWidget, { type: 'vision_risk' }>['data'] }) {
	return (
		<WidgetShell subtitle={data.note} title={`Vision risks (${data.warning_count})`}>
			{data.warnings.length ? (
				<Stack spacing={0.5}>
					{data.warnings.map((warning, index) => (
						<Typography key={index} color="text.secondary" sx={{ fontSize: '0.6875rem' }} variant="caption">
							{String(warning.slot_a ?? warning.slotA ?? '')} + {String(warning.slot_b ?? warning.slotB ?? '')}:{' '}
							{String(warning.product_a_name ?? warning.productAName ?? '')} /{' '}
							{String(warning.product_b_name ?? warning.productBName ?? '')}
						</Typography>
					))}
				</Stack>
			) : (
				<Typography color="text.secondary" sx={{ fontSize: '0.75rem' }} variant="body2">
					No adjacency warnings.
				</Typography>
			)}
		</WidgetShell>
	)
}

function PaymentFailuresWidget({ data }: { data: Extract<AssistantWidget, { type: 'payment_failures' }>['data'] }) {
	return (
		<WidgetShell subtitle={`${data.from} – ${data.to}`} title={`Failed payments (${data.total_failed})`}>
			<Stack spacing={0.5} sx={{ mb: 1 }}>
				{data.by_site.map((row) => (
					<Typography key={row.site_id} sx={{ fontSize: '0.75rem' }} variant="body2">
						{row.site_name ?? row.site_id}: {row.failed_count}
					</Typography>
				))}
			</Stack>
			{data.navigate ? (
				<Chip
					clickable
					color="error"
					component={RouterLink}
					label={data.navigate.label}
					size="small"
					to={normalizeAssistantNavigatePath(data.navigate.path)}
					variant="outlined"
				/>
			) : null}
		</WidgetShell>
	)
}

function ProductPlacementWidget({ data }: { data: Extract<AssistantWidget, { type: 'product_placement' }>['data'] }) {
	return (
		<WidgetShell subtitle={`${data.period.from} – ${data.period.to}`} title="Product placement">
			<Typography sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }} variant="caption">
				Top cabinets
			</Typography>
			<Stack spacing={0.5} sx={{ mb: 1 }}>
				{data.top_cabinets.map((cab, index) => (
					<Typography key={index} sx={{ fontSize: '0.75rem' }} variant="body2">
						{String(cab.site_name ?? cab.site_id)} • ${Number(cab.revenue_dollars ?? 0).toFixed(2)} •{' '}
						{String(cab.units ?? 0)} units
					</Typography>
				))}
			</Stack>
			{data.pair_suggestions.length ? (
				<>
					<Typography sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }} variant="caption">
						Pair with
					</Typography>
					{data.pair_suggestions.map((pair, index) => (
						<Typography key={index} color="text.secondary" sx={{ fontSize: '0.6875rem' }} variant="caption">
							{(pair.product_names as string[] | undefined)?.join(' + ') ?? 'Pair'} (lift {String(pair.lift ?? '—')})
						</Typography>
					))}
				</>
			) : null}
		</WidgetShell>
	)
}

function CabinetComparisonWidget({ data }: { data: Extract<AssistantWidget, { type: 'cabinet_comparison' }>['data'] }) {
	const exportRef = useRef<HTMLDivElement>(null)
	const chartData = useMemo(
		() =>
			data.cabinets.map((cab) => ({
				name: (cab.site_name ?? cab.site_id).slice(0, 14),
				revenue: cab.revenue_dollars ?? 0,
				fill: cab.fill_rate != null ? Math.round(cab.fill_rate * 100) : 0,
			})),
		[data.cabinets],
	)
	const csvRows = [
		['Cabinet', 'Revenue', 'Orders', 'Fill rate'],
		...data.cabinets.map((cab) => [
			cab.site_name ?? cab.site_id,
			String(cab.revenue_dollars ?? 0),
			String(cab.orders ?? 0),
			cab.fill_rate != null ? `${Math.round(cab.fill_rate * 100)}%` : '',
		]),
	]
	return (
		<WidgetShell
			csvRows={csvRows}
			exportRef={exportRef}
			filenameBase="cabinet-comparison"
			subtitle={`${data.from} – ${data.to}`}
			title="Cabinet comparison"
		>
			<Box sx={{ width: '100%', height: Math.max(160, chartData.length * 28 + 40) }}>
				<ResponsiveContainer height="100%" width="100%">
					<BarChart data={chartData} layout="vertical" margin={{ left: 4, right: 8, top: 4, bottom: 4 }}>
						<CartesianGrid strokeDasharray="3 3" horizontal={false} />
						<XAxis type="number" tickFormatter={(v) => `$${v}`} />
						<YAxis dataKey="name" type="category" width={88} tick={{ fontSize: 11 }} />
						<RechartsTooltip formatter={(value: number) => [`$${value.toFixed(2)}`, 'Revenue']} />
						<Bar dataKey="revenue" fill="var(--mui-palette-primary-main)" isAnimationActive={false} radius={[0, 4, 4, 0]} />
					</BarChart>
				</ResponsiveContainer>
			</Box>
		</WidgetShell>
	)
}

function StorePairMapWidget({ data }: { data: Extract<AssistantWidget, { type: 'store_pair_map' }>['data'] }) {
	const exportRef = useRef<HTMLDivElement>(null)
	const cabinets = data.cabinets?.length
		? data.cabinets
		: [
				...new Map(
					data.pairs.map((pair) => [
						String(pair.site_id),
						{ site_id: String(pair.site_id), site_name: String(pair.site_name ?? pair.site_id) },
					])
				).values(),
			]
	const csvRows = [
		['Pair', 'Cabinet', 'Placement', 'Lift'],
		...data.pairs.map((pair) => [
			((pair.product_names as string[] | undefined) ?? []).join(' + '),
			String(pair.site_name ?? pair.site_id),
			String(pair.placement_summary ?? pair.suggest_move ?? ''),
			String(pair.lift ?? ''),
		]),
	]

	return (
		<WidgetShell
			csvRows={csvRows}
			exportRef={exportRef}
			filenameBase="store-pair-map"
			subtitle={`${data.from} – ${data.to}`}
			title="Pair placement by cabinet"
		>
			<Box ref={exportRef}>
				<Stack direction="row" spacing={0.75} sx={{ mb: 1.25, flexWrap: 'wrap', pb: 0.5, rowGap: 0.75 }}>
					{cabinets.map((cab) => (
						<Box
							key={String(cab.site_id)}
							sx={{
								minWidth: 108,
								px: 1,
								py: 0.75,
								borderRadius: 1,
								border: '1px solid',
								borderColor: 'divider',
								bgcolor: 'action.hover',
							}}
						>
							<Typography sx={{ fontSize: '0.6875rem', fontWeight: 600 }} variant="caption">
								{String(cab.site_name ?? cab.site_id).slice(0, 16)}
							</Typography>
						</Box>
					))}
				</Stack>
				<Stack spacing={0.75}>
					{data.pairs.map((pair, index) => {
						const color = CABINET_PAIR_COLORS[index % CABINET_PAIR_COLORS.length]
						return (
							<Stack key={index} direction="row" spacing={1} alignItems="flex-start">
								<Box
									sx={{
										width: 8,
										height: 8,
										borderRadius: '50%',
										bgcolor: color,
										mt: 0.45,
										flexShrink: 0,
									}}
								/>
								<Box sx={{ minWidth: 0 }}>
									<Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }} variant="body2">
										{(pair.product_names as string[] | undefined)?.join(' + ') ?? 'Pair'} •{' '}
										{String(pair.site_name ?? pair.site_id)}
									</Typography>
									<Typography color="text.secondary" sx={{ fontSize: '0.6875rem' }} variant="caption">
										{String(pair.placement_summary ?? pair.suggest_move ?? '—')}
										{pair.lift != null ? ` • lift ${String(pair.lift)}` : ''}
									</Typography>
								</Box>
							</Stack>
						)
					})}
				</Stack>
			</Box>
		</WidgetShell>
	)
}

function PlanogramSwapsWidget({ data }: { data: Extract<AssistantWidget, { type: 'planogram_swaps' }>['data'] }) {
	if (!data.swaps.length) {
		const hasAnalyzedCells = (data.analysis?.cells_analyzed ?? 0) > 0
		return (
			<WidgetShell subtitle={hasAnalyzedCells ? data.note : undefined} title="Suggested swaps">
				<Typography color="text.secondary" sx={{ fontSize: '0.75rem' }} variant="body2">
					{hasAnalyzedCells
						? 'No underperforming slots found.'
						: data.note ?? 'No planogram cells with products to optimize.'}
				</Typography>
			</WidgetShell>
		)
	}
	return (
		<WidgetShell
			subtitle={`${data.swaps.length} slot${data.swaps.length === 1 ? '' : 's'} underperforming`}
			title="Suggested swaps"
		>
			<Stack spacing={1.5}>
				{data.swaps.map((swap) => (
					<ProductSwapCard
						currency={data.currency}
						fromName={swap.current_product_name ?? null}
						fromPhoto={swap.current_side_photo}
						fromRevenueCents={swap.current_revenue_cents}
						key={swap.cell_index}
						rationale={swap.rationale}
						slotLabel={swap.slot_label ?? `Cell ${swap.cell_index}`}
						toName={swap.suggested_product_name ?? null}
						toPhoto={swap.suggested_side_photo}
						toRevenueCents={swap.suggested_revenue_cents}
					/>
				))}
			</Stack>
		</WidgetShell>
	)
}

function PromotionPerformanceWidget({
	data,
}: {
	data: Extract<AssistantWidget, { type: 'promotion_performance' }>['data']
}) {
	return (
		<WidgetShell subtitle={data.note} title="Promotion performance">
			{data.items.length ? (
				<Stack spacing={0.75}>
					{data.items.map((item) => (
						<Stack key={item.voucher_id} direction="row" spacing={1} justifyContent="space-between" alignItems="center">
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }} variant="body2">
									{item.name ?? `Promotion #${item.voucher_id}`}
								</Typography>
								<Typography color="text.secondary" sx={{ fontSize: '0.6875rem' }} variant="caption">
									{item.discounted_orders ?? 0} orders • ${(item.total_discount_dollars ?? 0).toFixed(2)} discount
								</Typography>
							</Box>
							{item.navigate ? (
								<Chip
									clickable
									component={RouterLink}
									label="View"
									size="small"
									sx={{ flexShrink: 0 }}
									to={normalizeAssistantNavigatePath(item.navigate.path)}
									variant="outlined"
								/>
							) : null}
						</Stack>
					))}
				</Stack>
			) : (
				<Typography color="text.secondary" sx={{ fontSize: '0.75rem' }} variant="body2">
					No recent promotions in scope.
				</Typography>
			)}
		</WidgetShell>
	)
}

const PROMO_KIND_STYLES: Record<PromotionSuggestionItem['kind'], { label: string; color: 'warning' | 'info' }> = {
	slow_mover: { label: 'Slow mover', color: 'warning' },
	basket_pair: { label: 'Pair booster', color: 'info' },
}

function suggestionDraft(
	suggestion: PromotionSuggestionItem,
	stores: PromotionDraftAction['stores'],
): PromotionDraftAction {
	return {
		product_name: suggestion.product_name ?? '',
		discount_percent: suggestion.suggested_discount_percent,
		end_date: suggestion.end_date,
		selected_product_id: suggestion.product_id,
		selected_store_id: suggestion.suggested_store_id ?? null,
		products: [
			{
				id: suggestion.product_id,
				name: suggestion.product_name ?? 'Product',
				image: suggestion.image ?? null,
			},
		],
		stores,
		label: 'Create promotion',
	}
}

function PromotionSuggestionsWidget({
	data,
}: {
	data: Extract<AssistantWidget, { type: 'promotion_suggestions' }>['data']
}) {
	const [openProductId, setOpenProductId] = useState<number | null>(null)

	if (!data.suggestions.length) {
		return (
			<WidgetShell title="Promotion ideas">
				<Typography color="text.secondary" sx={{ fontSize: '0.75rem' }} variant="body2">
					{data.note ?? 'No promo candidates in this scope.'}
				</Typography>
			</WidgetShell>
		)
	}

	return (
		<WidgetShell
			subtitle={data.from && data.to ? `${data.from} – ${data.to} • click a card to edit` : 'Click a card to edit'}
			title="Promotion ideas"
		>
			<Stack spacing={0.75}>
				{data.suggestions.map((suggestion) => {
					const open = openProductId === suggestion.product_id
					const kind = PROMO_KIND_STYLES[suggestion.kind]
					return (
						<Box
							key={suggestion.product_id}
							onClick={() => setOpenProductId(open ? null : suggestion.product_id)}
							sx={{
								border: '1px solid',
								borderColor: open ? 'primary.main' : 'divider',
								borderRadius: 1.25,
								p: 1,
								cursor: 'pointer',
								bgcolor: open ? 'action.hover' : 'transparent',
								transition: 'border-color 120ms ease, background-color 120ms ease',
								'&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
							}}
						>
							<Stack alignItems="center" direction="row" spacing={1.25}>
								<Box
									sx={{
										width: 36,
										height: 36,
										flexShrink: 0,
										borderRadius: 1,
										bgcolor: 'var(--mui-palette-background-level2)',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										overflow: 'hidden',
									}}
								>
									{suggestion.image ? (
										<Box
											alt=""
											component="img"
											src={chipThumbnailUrl(suggestion.image, 72)}
											sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
										/>
									) : (
										<ImageIcon size={16} />
									)}
								</Box>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Stack alignItems="center" direction="row" spacing={0.75}>
										{kind ? (
											<Chip
												color={kind.color}
												label={kind.label}
												size="small"
												sx={{ height: 18, fontSize: '0.625rem', flexShrink: 0 }}
											/>
										) : null}
										<Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }} variant="body2">
											{suggestion.product_name ?? `Product ${suggestion.product_id}`}
										</Typography>
									</Stack>
									<Typography
										color="text.secondary"
										sx={{ fontSize: '0.6875rem', display: 'block', mt: 0.25 }}
										variant="caption"
									>
										{suggestion.reason}
										{suggestion.suggested_store_name ? ` • ${suggestion.suggested_store_name}` : ''}
									</Typography>
								</Box>
								<Stack alignItems="center" direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
									<Chip
										color="primary"
										label={`${suggestion.suggested_discount_percent}% off`}
										size="small"
										variant={open ? 'filled' : 'outlined'}
									/>
									<CaretDownIcon
										size={14}
										style={{
											transform: open ? 'rotate(180deg)' : 'none',
											transition: 'transform 120ms ease',
										}}
									/>
								</Stack>
							</Stack>
							{open ? (
								<Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
									<PromotionFormWidget draft={suggestionDraft(suggestion, data.stores)} />
								</Box>
							) : null}
						</Box>
					)
				})}
			</Stack>
		</WidgetShell>
	)
}

function renderWidget(widget: AssistantWidget): ReactElement | null {
	switch (widget.type) {
		case 'period_comparison':
			return <PeriodComparisonWidget key="period_comparison" data={widget.data} />
		case 'restock_priorities':
			return <RestockPrioritiesWidget key="restock_priorities" data={widget.data} />
		case 'store_visit_plan':
			return <StoreVisitPlanWidget key="store_visit_plan" data={widget.data} />
		case 'pending_planogram':
			return <PendingPlanogramWidget key="pending_planogram" data={widget.data} />
		case 'vision_risk':
			return <VisionRiskWidget key="vision_risk" data={widget.data} />
		case 'payment_failures':
			return <PaymentFailuresWidget key="payment_failures" data={widget.data} />
		case 'product_placement':
			return <ProductPlacementWidget key="product_placement" data={widget.data} />
		case 'cabinet_comparison':
			return <CabinetComparisonWidget key="cabinet_comparison" data={widget.data} />
		case 'store_pair_map':
			return <StorePairMapWidget key="store_pair_map" data={widget.data} />
		case 'promotion_performance':
			return <PromotionPerformanceWidget key="promotion_performance" data={widget.data} />
		case 'planogram_swaps':
			return <PlanogramSwapsWidget key="planogram_swaps" data={widget.data} />
		case 'promotion_suggestions':
			return <PromotionSuggestionsWidget key="promotion_suggestions" data={widget.data} />
		default:
			return null
	}
}

export function ChatAssistantWidget({ widget }: { widget: AssistantWidget }): ReactElement | null {
	return renderWidget(widget)
}
